import client from '@/lib/axios-client'
import type {
	ApiResponse,
	CartaoAmarelo,
	CartaoAmareloAllListOutput,
	CartaoAmareloAllListParams,
	CartaoAmareloCreateInput,
	CartaoAmareloListOutput,
	CartaoAmareloListParams,
	CartaoAmareloMeListOutput,
	CartaoAmareloMeListParams,
	CartaoAmareloUpdateInput,
} from '@/types'
import {
	assertUploadSizeWithinLimit,
	DEFAULT_MAX_SIZE_MB,
	optimizeImageForUpload,
	sendWithCompressionRetry,
} from '@/utils/image-compression'

const BASE_URL = '/cartao-amarelo'
const ME_BASE_URL = '/cartao-amarelo/me'
const ALL_BASE_URL = '/cartao-amarelo/all'

export async function getCartaoAmareloList(
	params?: CartaoAmareloListParams,
): Promise<ApiResponse<CartaoAmareloListOutput>> {
	const response = await client.get(BASE_URL, { params })
	return response.data
}

export async function getCartaoAmareloMeList(
	params?: CartaoAmareloMeListParams,
): Promise<ApiResponse<CartaoAmareloMeListOutput>> {
	const response = await client.get(ME_BASE_URL, { params })
	return response.data
}

export async function getCartaoAmareloAllList(
	params?: CartaoAmareloAllListParams,
): Promise<ApiResponse<CartaoAmareloAllListOutput>> {
	const response = await client.get(ALL_BASE_URL, { params })
	return response.data
}

export async function createCartaoAmarelo(
	input: CartaoAmareloCreateInput,
): Promise<ApiResponse<CartaoAmarelo>> {
	const send = async (maxSizeMB: number) => {
		const formData = new FormData()

		for (const key of Object.keys(
			input,
		) as (keyof CartaoAmareloCreateInput)[]) {
			const val = input[key]
			if (val === undefined || val === null) {
				continue
			}
			if (key === 'image' && val instanceof File) {
				const optimizedImage = await optimizeImageForUpload(val, maxSizeMB)
				assertUploadSizeWithinLimit([optimizedImage])
				formData.append('image', optimizedImage)
			} else {
				formData.append(key, String(val))
			}
		}

		const response = await client.post(BASE_URL, formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		})
		return response.data
	}

	// Sem imagem, recomprimir nao resolve um 413 — envia sem retry
	return input.image instanceof File
		? sendWithCompressionRetry(send)
		: send(DEFAULT_MAX_SIZE_MB)
}

export async function updateCartaoAmarelo(
	id: string,
	data: CartaoAmareloUpdateInput,
): Promise<CartaoAmarelo> {
	const send = async (maxSizeMB: number) => {
		const formData = new FormData()

		for (const key of Object.keys(data) as (keyof CartaoAmareloUpdateInput)[]) {
			const val = data[key]
			if (val === undefined || val === null) {
				continue
			}
			if (key === 'image' && val instanceof File) {
				const optimizedImage = await optimizeImageForUpload(val, maxSizeMB)
				assertUploadSizeWithinLimit([optimizedImage])
				formData.append('image', optimizedImage)
			} else {
				formData.append(key, String(val))
			}
		}

		const response = await client.patch(`${BASE_URL}/${id}`, formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		})
		return response.data.data
	}

	return data.image instanceof File
		? sendWithCompressionRetry(send)
		: send(DEFAULT_MAX_SIZE_MB)
}

export async function deleteCartaoAmarelo(id: string): Promise<void> {
	await client.delete(`${BASE_URL}/${id}`)
}
